/**
 * MQTT Client for DOOH Player
 *
 * Connects to AWS IoT Core via WebSocket using SigV4-signed URLs.
 * Handles:
 * - Connection with LWT (Last Will and Testament)
 * - Subscription to schedules and commands topics
 * - Exponential backoff reconnection
 * - WSS URL refresh every 12 hours
 *
 * (GEN-128) There is no periodic MQTT telemetry/heartbeat publish here — the
 * CMS marks a screen ONLINE only via REST `POST /api/heartbeat`
 * (useRestHeartbeat); an MQTT telemetry topic had no server-side consumer and
 * was removed.
 */

import mqtt, { type MqttClient as MqttClientType } from 'mqtt';
import { fetchWsUrl } from '../api';
import {
  type MqttClientConfig,
  type MqttConnectionState,
  type PlayerCommand,
} from '../types';

/** Default configuration values */
const DEFAULTS = {
  urlRefreshIntervalMs: 12 * 60 * 60 * 1_000, // 12 hours
  reconnectBaseDelayMs: 1_000, // 1 second
  reconnectMaxDelayMs: 60_000, // 60 seconds
  maxReconnectAttempts: 20,
};

/** Slow retry interval after fast reconnects exhausted (#26) — 5 minutes */
const SLOW_RETRY_INTERVAL_MS = 5 * 60 * 1_000;

/**
 * Refresh the signed URL slightly before it actually expires so a reconnect
 * never races the expiry boundary. The signed WSS URL is valid for 24h
 * (WSS_URL_EXPIRY_HOURS); reconnects reuse the cached URL until it is within
 * this buffer of expiry, which is what stops the `POST /api/player/ws-url`
 * 429 storm — the endpoint is rate-limited to 1 req/device/60s.
 */
const URL_EXPIRY_BUFFER_MS = 60_000; // 1 minute

/** Callback types */
export type MessageCallback = (topic: string, payload: Uint8Array) => void;
export type StateChangeCallback = (state: MqttConnectionState) => void;
export type ErrorCallback = (error: Error) => void;
export type CommandCallback = (command: PlayerCommand) => void;
export type ScheduleCallback = (manifest: unknown) => void;

export interface MqttClientState {
  connectionState: MqttConnectionState;
  reconnectAttempts: number;
  lastConnectedAt: number | null;
}

export interface MqttClient {
  connect(): Promise<void>;
  disconnect(): void;
  getState(): MqttClientState;
  onMessage(cb: MessageCallback): void;
  onCommand(cb: CommandCallback): void;
  onSchedule(cb: ScheduleCallback): void;
  onStateChange(cb: StateChangeCallback): void;
  onError(cb: ErrorCallback): void;
  publish(topic: string, payload: string | Uint8Array, qos?: 0 | 1 | 2): void;
}

export function createMqttClient(config: MqttClientConfig): MqttClient {
  const {
    jwt,
    deviceId,
    organizationId,
    urlRefreshIntervalMs = DEFAULTS.urlRefreshIntervalMs,
    reconnectBaseDelayMs = DEFAULTS.reconnectBaseDelayMs,
    reconnectMaxDelayMs = DEFAULTS.reconnectMaxDelayMs,
    maxReconnectAttempts = DEFAULTS.maxReconnectAttempts,
  } = config;

  // ─── Internal state ──────────────────────────────────────────────
  let mqttClient: MqttClientType | null = null;
  let connectionState: MqttConnectionState = 'disconnected';
  let reconnectAttempts = 0;
  let lastConnectedAt: number | null = null;
  let signedUrl: string | null = null;
  /** Epoch ms when the cached signed URL expires (from the ws-url response). */
  let signedUrlExpiresAt: number | null = null;

  // Timers
  let urlRefreshTimer: ReturnType<typeof setInterval> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let intentionalDisconnect = false;
  /**
   * True while a connection attempt is in flight (from the moment we begin
   * fetching/using a URL until the socket either connects or closes). This is
   * the guard that guarantees a SINGLE reconnect scheduler: no new attempt is
   * scheduled while one is already running.
   */
  let connectInFlight = false;

  // Callbacks
  let messageCallback: MessageCallback | null = null;
  let commandCallback: CommandCallback | null = null;
  let scheduleCallback: ScheduleCallback | null = null;
  let stateChangeCallback: StateChangeCallback | null = null;
  let errorCallback: ErrorCallback | null = null;

  // ─── Topic names ─────────────────────────────────────────────────
  const baseTopic = `dooh/${organizationId}/${deviceId}`;
  const schedulesTopic = `${baseTopic}/schedules`;
  const commandsTopic = `${baseTopic}/commands`;
  const statusTopic = `${baseTopic}/status`;

  // ─── Helpers ──────────────────────────────────────────────────────

  function setState(newState: MqttConnectionState) {
    connectionState = newState;
    stateChangeCallback?.(newState);
  }

  function clearTimers() {
    if (urlRefreshTimer) {
      clearInterval(urlRefreshTimer);
      urlRefreshTimer = null;
    }
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  /**
   * Fully tear down the current mqtt.js client so a stale client can never fire
   * `close`/`error`/`offline` events that would spawn additional reconnect
   * loops. Every fresh connection attempt starts by calling this.
   */
  function teardownClient() {
    if (mqttClient) {
      mqttClient.removeAllListeners();
      try {
        mqttClient.end(true);
      } catch {
        // ignore — client may already be ending
      }
      mqttClient = null;
    }
  }

  /** True when we hold a cached signed URL that is still comfortably valid. */
  function isCachedUrlValid(): boolean {
    if (!signedUrl || signedUrlExpiresAt === null) return false;
    return Date.now() < signedUrlExpiresAt - URL_EXPIRY_BUFFER_MS;
  }

  /**
   * Ensure a usable signed WSS URL is available. Reuses the cached URL while it
   * is still valid (the URL is good for 24h) so reconnect attempts do NOT hammer
   * the rate-limited `POST /api/player/ws-url` endpoint. Only fetches a fresh
   * URL when forced, or when none is cached / it is expired or near expiry.
   */
  async function ensureSignedUrl(force = false): Promise<void> {
    if (!force && isCachedUrlValid()) return;
    const response = await fetchWsUrl(jwt);
    signedUrl = response.url;
    const parsed = Date.parse(response.expiresAt);
    signedUrlExpiresAt = Number.isNaN(parsed) ? null : parsed;
  }

  function publishStatus(status: 'online' | 'offline') {
    if (!mqttClient?.connected) return;
    const payload = JSON.stringify({
      deviceId,
      timestamp: new Date().toISOString(),
      status,
    });
    mqttClient.publish(statusTopic, payload, { qos: 1, retain: true }, (err) => {
      if (err) {
        console.warn(`[MQTT] Status publish (${status}) failed:`, err.message);
      }
    });
  }

  function handleIncomingMessage(topic: string, payload: Uint8Array) {
    // Forward raw message
    messageCallback?.(topic, payload);

    // Parse commands
    if (topic === commandsTopic) {
      try {
        const command = JSON.parse(new TextDecoder().decode(payload)) as PlayerCommand;
        commandCallback?.(command);
      } catch {
        console.warn('[MQTT] Failed to parse command payload:', topic);
      }
    }

    // Parse schedule manifests (#24) — previously the schedules topic was
    // subscribed but never parsed, so manifests were never delivered.
    if (topic === schedulesTopic) {
      try {
        const manifest = JSON.parse(new TextDecoder().decode(payload));
        scheduleCallback?.(manifest);
      } catch {
        console.warn('[MQTT] Failed to parse schedule payload:', topic);
      }
    }
  }

  function subscribeTopics() {
    if (!mqttClient?.connected) return;

    mqttClient.subscribe(schedulesTopic, { qos: 1 }, (err) => {
      if (err) {
        console.warn('[MQTT] Failed to subscribe to schedules topic:', err.message);
      } else {
        console.log('[MQTT] Subscribed to', schedulesTopic);
      }
    });

    mqttClient.subscribe(commandsTopic, { qos: 1 }, (err) => {
      if (err) {
        console.warn('[MQTT] Failed to subscribe to commands topic:', err.message);
      } else {
        console.log('[MQTT] Subscribed to', commandsTopic);
      }
    });
  }

  function startUrlRefresh() {
    stopUrlRefresh();
    urlRefreshTimer = setInterval(() => {
      console.log('[MQTT] Refreshing WSS URL, reconnecting...');

      // Reset the fast-backoff cycle for a scheduled refresh, then route the
      // reconnect through the SAME resilient path as a normal reconnect —
      // runReconnect(true) forces a fresh signed URL and, crucially, on failure
      // calls scheduleReconnect() so a single refresh failure re-schedules a
      // backoff retry.
      //
      // (#169) Previously this called doConnect() directly: if the refreshed
      // connection closed before it connected, the 'close' handler cleared this
      // refresh interval and rejected with nothing left to drive a retry —
      // no reconnectTimer, no refresh interval, connectInFlight=false → MQTT
      // stayed permanently dead until a page reload. Going through runReconnect
      // guarantees the failed-refresh path always ends with a pending reconnect
      // (or a live connection), never a dead end.
      reconnectAttempts = 0;
      void runReconnect(true);
    }, urlRefreshIntervalMs);
  }

  function stopUrlRefresh() {
    if (urlRefreshTimer) {
      clearInterval(urlRefreshTimer);
      urlRefreshTimer = null;
    }
  }

  /**
   * Run a single reconnect attempt: reuse the cached signed URL when still
   * valid (only refetch when forced/expired), then (re)connect. On failure the
   * next attempt is scheduled — never more than one at a time.
   */
  async function runReconnect(forceUrlRefresh = false): Promise<void> {
    connectInFlight = true;
    try {
      await ensureSignedUrl(forceUrlRefresh);
      await doConnect();
    } catch (err) {
      connectInFlight = false;
      console.warn('[MQTT] Reconnect attempt failed:', err);
      scheduleReconnect();
    }
  }

  /**
   * Schedule exactly ONE reconnect attempt.
   *
   * This is the single reconnect scheduler for the client. Overlapping loops
   * (the original bug — multiple concurrent backoff timers all reconnecting
   * with the same clientId, which AWS IoT Core then kicks off one another) are
   * prevented by two guards: we never schedule while a timer is already pending,
   * and never while a connection attempt is already in flight.
   */
  function scheduleReconnect() {
    if (intentionalDisconnect) return;
    // SINGLE-SCHEDULER GUARANTEE: at most one pending timer / in-flight attempt.
    if (reconnectTimer !== null || connectInFlight) return;

    // After exhausting fast reconnect attempts, fall back to a slow periodic
    // retry (#26). An unattended kiosk must NEVER permanently give up — it
    // keeps trying every 5 minutes so it recovers when the network returns.
    if (reconnectAttempts >= maxReconnectAttempts) {
      console.warn(
        `[MQTT] Max reconnect attempts (${maxReconnectAttempts}) reached. ` +
        `Switching to slow retry (every ${SLOW_RETRY_INTERVAL_MS / 1000}s).`,
      );
      setState('disconnected');
      errorCallback?.(new Error(`MQTT connection failed after ${maxReconnectAttempts} attempts; will retry`));

      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (intentionalDisconnect) return;
        console.log('[MQTT] Slow retry: attempting reconnect...');
        // Reset attempts so the fast backoff cycle runs again before
        // falling back to another slow retry. Force a fresh URL — a long
        // outage may well have outlived the cached URL.
        reconnectAttempts = 0;
        setState('reconnecting');
        void runReconnect(true);
      }, SLOW_RETRY_INTERVAL_MS);
      return;
    }

    setState('reconnecting');

    // Exponential backoff with jitter
    const delay = Math.min(
      reconnectBaseDelayMs * Math.pow(2, reconnectAttempts),
      reconnectMaxDelayMs,
    );
    // Add random jitter (0-25% of delay)
    const jitter = delay * 0.25 * Math.random();
    const totalDelay = delay + jitter;

    console.log(
      `[MQTT] Reconnecting in ${Math.round(totalDelay)}ms (attempt ${reconnectAttempts + 1}/${maxReconnectAttempts})`,
    );

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      reconnectAttempts++;
      void runReconnect();
    }, totalDelay);
  }

  async function doConnect(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!signedUrl) {
        connectInFlight = false;
        reject(new Error('No signed URL available'));
        return;
      }

      // A disconnect() may have raced this in-flight connect while the signed
      // URL was being fetched/awaited (e.g. the useMqtt effect re-ran). If the
      // caller has already asked to disconnect, do NOT open a socket at all —
      // otherwise we'd leak a live client with a duplicate clientId.
      if (intentionalDisconnect) {
        connectInFlight = false;
        resolve();
        return;
      }

      // Tear down any previous client FIRST so its listeners can never fire
      // events that would spawn a competing reconnect loop.
      teardownClient();

      connectInFlight = true;
      setState('connecting');

      // Ensures this attempt's promise settles exactly once, and lets us tell a
      // "closed before we ever connected" (attempt failed → reject) apart from a
      // "dropped after being connected" (schedule a reconnect).
      let settled = false;

      const lwtPayload = JSON.stringify({
        deviceId,
        timestamp: new Date().toISOString(),
        status: 'offline',
      });

      const client = mqtt.connect(signedUrl, {
        clientId: deviceId,
        clean: true,
        reconnectPeriod: 0, // We handle reconnection ourselves
        will: {
          topic: statusTopic,
          payload: lwtPayload,
          qos: 1,
          retain: true,
        },
      });
      mqttClient = client;

      client.on('connect', () => {
        // A disconnect() raced this in-flight connect: the socket opened but the
        // caller has already abandoned it. Tear the just-created client down and
        // bail — never subscribe or start the urlRefresh timer on it, or
        // we leak a live connection with a duplicate clientId.
        if (intentionalDisconnect) {
          settled = true;
          connectInFlight = false;
          teardownClient();
          clearTimers();
          resolve();
          return;
        }

        console.log('[MQTT] Connected to AWS IoT Core');
        connectInFlight = false;
        settled = true;
        setState('connected');
        lastConnectedAt = Date.now();
        reconnectAttempts = 0;

        // Subscribe to topics
        subscribeTopics();

        // Publish online status
        publishStatus('online');

        // Start URL refresh
        startUrlRefresh();

        resolve();
      });

      client.on('message', (topic: string, payload: Buffer) => {
        handleIncomingMessage(topic, new Uint8Array(payload));
      });

      client.on('error', (err: Error) => {
        console.error('[MQTT] Connection error:', err.message);
        errorCallback?.(err);
      });

      client.on('close', () => {
        console.log('[MQTT] Connection closed');
        stopUrlRefresh();
        connectInFlight = false;

        // Closed before we ever connected → this attempt failed. Reject so the
        // caller (runReconnect / connect) drives the next scheduled attempt.
        if (!settled) {
          settled = true;
          reject(new Error('MQTT connection closed before connect'));
          return;
        }

        // Dropped after a healthy connection → schedule a single reconnect.
        if (!intentionalDisconnect) {
          setState('disconnected');
          scheduleReconnect();
        }
      });

      client.on('offline', () => {
        console.log('[MQTT] Client offline');
        setState('reconnecting');
      });
    });
  }

  // ─── Public API ───────────────────────────────────────────────────

  return {
    async connect() {
      intentionalDisconnect = false;

      try {
        // Fetch a fresh signed WSS URL on an explicit connect.
        await ensureSignedUrl(true);
        await doConnect();
      } catch (err) {
        // Initial connection failed — start the (single) reconnect loop so an
        // unattended kiosk recovers, then surface the error to the caller.
        connectInFlight = false;
        scheduleReconnect();
        throw err;
      }
    },

    disconnect() {
      intentionalDisconnect = true;
      connectInFlight = false;
      clearTimers();

      // Publish offline status before disconnecting (graceful)
      publishStatus('offline');

      teardownClient();

      setState('disconnected');
    },

    getState() {
      return {
        connectionState,
        reconnectAttempts,
        lastConnectedAt,
      };
    },

    onMessage(cb: MessageCallback) {
      messageCallback = cb;
    },

    onCommand(cb: CommandCallback) {
      commandCallback = cb;
    },

    onSchedule(cb: ScheduleCallback) {
      scheduleCallback = cb;
    },

    onStateChange(cb: StateChangeCallback) {
      stateChangeCallback = cb;
    },

    onError(cb: ErrorCallback) {
      errorCallback = cb;
    },

    publish(topic: string, payload: string | Uint8Array, qos: 0 | 1 | 2 = 0) {
      if (!mqttClient?.connected) {
        console.warn('[MQTT] Cannot publish, not connected');
        return;
      }
      const data = typeof payload === 'string' ? payload : Buffer.from(payload);
      mqttClient.publish(topic, data, { qos }, (err) => {
        if (err) {
          console.warn('[MQTT] Publish failed:', err.message);
        }
      });
    },
  };
}
